import { useCallback, useMemo, useRef, useState } from 'react';
import type { FC } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDebounceCallback, useResizeObserver } from 'usehooks-ts';
import { Button, Text } from '../../../ui-components';
import { GetIcon } from '../../icons';
import { LARGER_DEVICE_IMAGES_TO_SHOW } from '../constants';
import { AssetInfoPropTypes, AssetType } from '../utils';
import { AssetColumn } from './AssetColumn';
import type { AssetInfo } from '../utils';

interface Size {
    width?: number;
    height?: number;
}

interface TwoColumnViewProps {
    assets: AssetInfo[];
}

export const TwoColumnView: FC<TwoColumnViewProps> = ({ assets }) => {
    const { isOpen, onToggle } = useDisclosure();
    const leftColumn = useMemo(() => assets.filter((_, idx) => idx % 2 === 0), [assets]);
    const rightColumn = useMemo(() => assets.filter((_, idx) => idx % 2 === 1), [assets]);
    const hasBrandSpecialistVideo = useMemo(
        () => assets.some((a) => a.assetType === AssetType.BRAND_SPECIALIST_VIDEO),
        [assets],
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const [{ width: containerWidth }, setSize] = useState<Size>({
        width: undefined,
        height: undefined,
    });
    const imageHeight = useMemo(
        () => (containerWidth && containerWidth > 8 ? (containerWidth - 8) / 2.0 : undefined),
        [containerWidth],
    );
    const onResize = useDebounceCallback(setSize, 200);

    useResizeObserver({
        ref: containerRef,
        onResize,
    });

    const handleOnToggle = useCallback(() => {
        if (isOpen) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
        onToggle();
    }, [onToggle, isOpen]);

    const collapsedHeight =
        !isOpen && !!imageHeight
            ? // Get the visible images height:rendered image height + gap btw images
              imageHeight * LARGER_DEVICE_IMAGES_TO_SHOW + (LARGER_DEVICE_IMAGES_TO_SHOW - 1) * 8
            : undefined;

    return (
        <div
            className="flex flex-col space-y-4"
            data-testid="photo-grid-two-column-view"
            ref={containerRef}
        >
            <div
                className="max-h-[8000px] overflow-hidden transition-[max-height] duration-500"
                style={collapsedHeight ? { maxHeight: collapsedHeight } : undefined}
            >
                <div className="flex space-x-2">
                    <AssetColumn
                        dataTestId="photo-grid-column-1"
                        assets={leftColumn}
                        isMainColumn={!hasBrandSpecialistVideo}
                    />
                    <AssetColumn
                        dataTestId="photo-grid-column-2"
                        assets={rightColumn}
                        isMainColumn={hasBrandSpecialistVideo}
                    />
                </div>
            </div>
            {assets.length > 5 ? (
                <div className="flex justify-center">
                    <Button variant="tertiary" onClick={handleOnToggle}>
                        <div className="flex items-center space-x-1">
                            <Text as="span" size="base" style="bold" color="slt-pepper.1000">
                                {isOpen ? (
                                    <FormattedMessage
                                        id="photo.grid.show.less"
                                        defaultMessage="Show less"
                                    />
                                ) : (
                                    <FormattedMessage
                                        id="photo.grid.show.more"
                                        defaultMessage="Show more"
                                    />
                                )}
                            </Text>
                            <div
                                className={`flex justify-center transition-transform ${
                                    isOpen ? '-rotate-180' : ''
                                }`}
                            >
                                <GetIcon name="dropdown-arrow-open" size="md" />
                            </div>
                        </div>
                    </Button>
                </div>
            ) : null}
        </div>
    );
};

TwoColumnView.propTypes = {
    assets: PropTypes.arrayOf(AssetInfoPropTypes.isRequired).isRequired,
};
